import React, { useEffect, useRef, useState } from "react";
import BulkEditor from "./BulkEditor";
import AssetRow from "./AssetRow";
import useAds from "@hooks/useAds";
import { useSelector } from "react-redux";
import useDefaultValues from "@hooks/useDefaultValues";
import useRQAdLaunches from "@hooks/useRQAdLaunches";
import Navbar from "@components/Navbar/Navbar";
import { Loader } from "@components/Loader";
import { notify } from "@utils/notify";
import { useNavigate } from "react-router-dom";
import { extractDriveId } from "@components/SortableItem/helper";
import useUser from "@hooks/useUser";
import AdPostingValidation from "@components/AdPostingValidation/AdPostingValidation";
import { fetchAdsLaunchValidationErrors } from "@services/validateFbAdsLaunch";

const AssetMapper = () => {
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const { handleGetUserById } = useUser();
  const adAccountId = localStorage.getItem("adAccountId");
  const targetCampaignId = localStorage.getItem("targetCampaignId");
  const targetAdsetId = localStorage.getItem("targetAdsetId");
  const targetAdsetName = localStorage.getItem("targetAdsetName");
  const targetCampaignName = localStorage.getItem("targetCampaignName");
  const adAccountName = localStorage.getItem("adAccountName");
  const campaignObjective = localStorage.getItem("campaignObjective");

  // states
  const [allSelected, setAllSelected] = useState(false);
  const [selectedAds, setSelectedAds] = useState({});
  const [adAndAdsetMapping, setAdAndAdsetMapping] = useState([]);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [campaignNames, setCampaignNames] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add state variables for scheduling
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [scheduledDateForModal, setScheduledDateForModal] = useState(null);

  // Add state for validation modal
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  // Add state for publish validation modal
  const [isPublishValidationModalOpen, setIsPublishValidationModalOpen] = useState(false);
  const [publishValidationErrors, setPublishValidationErrors] = useState([]);
  const [isPublishValidating, setIsPublishValidating] = useState(false);

  // Floating bottom bar state
  const [showFloatingBar, setShowFloatingBar] = useState(false);
  const [floatingSelection, setFloatingSelection] = useState({
    campaignId: "",
    campaign: null,
    adsetId: "",
    adset: null,
  });
  const bulkEditorSentinelRef = useRef(null);

  // hooks
  const { handleGetAdsWithCreatives } = useAds();
  const { handleGetDefaultValues } = useDefaultValues();
  const { handleSaveAdLaunches } = useRQAdLaunches();

  // selectors
  const { defaultValues } = useSelector((state) => state.defaultValues) ?? {};
  const { ads } = useSelector((state) => state.ads);
  const { campaigns } = useSelector((state) => state.campaigns);

  // handlers
  const handleToggleAll = () => {
    const newSelectedState = !allSelected;
    setAllSelected(newSelectedState);

    if (ads && ads.length > 0) {
      if (newSelectedState) {
        const allSelectedAds = {};
        ads.forEach((ad) => {
          allSelectedAds[ad._id] = true;
        });
        setSelectedAds(allSelectedAds);
      } else {
        setSelectedAds({});
      }
    }
  };

  const handleToggleAsset = (creativeId) => {
    setSelectedAds((prev) => ({
      ...prev,
      [creativeId]: !prev[creativeId],
    }));
  };

  const handleUpdateAdSets = (creativeId, adSets) => {
    setAdAndAdsetMapping((prev) =>
      prev.filter(
        (mapping) =>
          !(mapping.adId === creativeId && !adSets.includes(mapping.adsetName))
      )
    );
  };

  const handleApply = (
    selectedAdsetId,
    selectedAdset,
    selectedCampaignId,
    selectedCampaign
  ) => {
    const arrOfSelectedAds = Object.keys(selectedAds).filter(
      (id) => selectedAds[id]
    );

    if (overwriteExisting) {
      setAdAndAdsetMapping((prev) => {
        const filteredPrev = prev.filter(
          (mapping) => !arrOfSelectedAds.includes(mapping.adId)
        );
        const newMapping = arrOfSelectedAds.map((adId) => ({
          adId,
          adsetId: selectedAdsetId,
          adsetName: selectedAdset?.name,
          fbCampaignId: selectedCampaignId,
          campaignName: selectedCampaign?.name,
          campaignType: selectedCampaign?.objective || "OUTCOME_SALES",
        }));
        return [...filteredPrev, ...newMapping];
      });
      // setSelectedAds({});
      // setAllSelected(false);
      return;
    }

    const newMapping = arrOfSelectedAds.map((adId) => ({
      adId,
      adsetId: selectedAdsetId,
      adsetName: selectedAdset?.name,
      fbCampaignId: selectedCampaignId,
      campaignName: selectedCampaign?.name,
      campaignType: selectedCampaign?.objective || "OUTCOME_SALES",
    }));

    setAdAndAdsetMapping((prev) => [...prev, ...newMapping]);
    setCampaignNames((prev) => [...prev, selectedCampaign?.name]);
    // setSelectedAds({});
    // setAllSelected(false);
  };

  const prepareAdLaunchData = () => {
    const fbPageId = localStorage.getItem("selectedFacebookPageId");
    const instagramPageId = localStorage.getItem("selectedInstagramPageId");
    // console.log("fbPageId", fbPageId);
    // console.log("instagramPageId", instagramPageId);
    const adObjects = adAndAdsetMapping.map((mapping) => ({
      adId: mapping.adId,
      fbAdsetId: mapping.adsetId,
      fbAdAccountId: adAccountId || "",
      fbCampaignId: mapping?.fbCampaignId || "",
      userId: userId,
      fbCampaignType: mapping?.campaignType || "OUTCOME_SALES",
      fbPageId: fbPageId || "",
      fbInstagramId: instagramPageId || "",
      fbAdsetName: mapping.adsetName || targetAdsetName,
      fbCampaignName: mapping.campaignName || targetCampaignName,
    }));
    return adObjects;
  };

  const handlePublish = async (validate = false) => {
    try {
      setLoading(true);
      const launchData = prepareAdLaunchData();
      if (launchData.length === 0) {
        notify("Please map at least one ad to an adset", "error");
        return;
      }
      
      const publishPayload = { adObjects: launchData };

      // if (validate) {
      //   // First, check for validation errors using the API
      //   const errors = await checkForValidationErrors(publishPayload);
      //   if (errors.length > 0) {
      //     setPublishValidationErrors(errors);
      //     setIsPublishValidationModalOpen(true);
      //     return;
      //   }
      // }

      // set publish Payload in local storage for review page
      localStorage.setItem("publishPayloadFromAdvMapping", JSON.stringify(publishPayload));
      localStorage.setItem("existingAdAdsetMapping", JSON.stringify(adAndAdsetMapping));

      const campaignObjective = localStorage.getItem("campaignObjective")
      if (campaignObjective === "OUTCOME_APP_PROMOTION") {
        navigate("/review-default-values-ap", { state: { fromPage: "advanced-mapping" } });
      } else {
        navigate("/review-default-values", { state: { fromPage: "advanced-mapping" } });
      }

      // If no errors, proceed with publish
      // const response = await handleSaveAdLaunches(publishPayload);
      // if (response && response.batch_id) {
      //   localStorage.removeItem("advancedGroupMapping");
      //   navigate("/ads-progress", {
      //     state: {
      //       batchId: response.batch_id,
      //       launches: response.launches,
      //     },
      //   });
      // }
    } catch (error) {
      console.error("Error in handlePublish:", error);
      notify("An error occurred while publishing. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add validation check function
  const checkForValidationErrors = async (schedulePayload) => {
    try {
      setIsValidating(true);
      const response = await fetchAdsLaunchValidationErrors(schedulePayload);

      if (response.data && response.data.validation && !response.data.validation.is_valid) {
        const errors = response.data.validation.error.map((error, index) => ({
          id: index + 1,
          error: error,
          errorDetails: response.data.validation.error_details[index] || "",
          action: response.data.validation.actions[index] || ""
        }));
        return errors;
      }

      return [];
    } catch (error) {
      console.error("Error validating ads launch:", error);
      notify("Error validating ads launch", "error");
      return [];
    } finally {
      setIsValidating(false);
    }
  };

  // Add scheduling functionality
  const handleScheduleAds = async () => {
    // Prepare the request with the correct format
    const launchData = prepareAdLaunchData();

    if (launchData.length === 0) {
      notify("Failed to prepare ad launch data", "error");
      return;
    }

    const schedulePayload = {
      adObjects: launchData.map((adObject) => ({
        ...adObject,
      })),
    };

    // First, check for validation errors using the API
    const errors = await checkForValidationErrors(schedulePayload);

    if (errors.length > 0) {
      // Show validation modal if there are errors
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    // If no errors, proceed with original scheduling logic
    const userConfirmed = window.confirm(
      "Are you sure you want to schedule these ads?"
    );

    if (userConfirmed) {
      // Get the current date/time for scheduling
      const scheduledDate = new Date();
      scheduledDate.setMinutes(scheduledDate.getMinutes() + 2);

      // Set the default date for the modal
      setScheduledDateForModal(scheduledDate);

      // Open the modal
      setIsScheduleModalOpen(true);
    }
  };

  // Handle validation modal actions
  const handleValidationEditAds = () => {
    setIsValidationModalOpen(false);

    // Proceed with scheduling despite validation errors
    const userConfirmed = window.confirm(
      "Are you sure you want to schedule these ads?"
    );

    if (userConfirmed) {
      // Get the current date/time for scheduling
      const scheduledDate = new Date();
      scheduledDate.setMinutes(scheduledDate.getMinutes() + 2);

      // Set the default date for the modal
      setScheduledDateForModal(scheduledDate);

      // Open the modal
      setIsScheduleModalOpen(true);
    }
  };

  // Handle publish validation modal actions
  const handlePublishValidationEditAds = () => {
    setIsPublishValidationModalOpen(false);
    handlePublish(false);
  };

  const handleValidationResolve = async () => {
    // Prepare the same payload for re-validation
    setIsValidationModalOpen(false); // Close modal immediately when spinner starts
    setIsValidating(true); // Show spinner
    const launchData = prepareAdLaunchData();
    const schedulePayload = { adObjects: launchData };

    // Re-check validation and proceed if resolved
    const errors = await checkForValidationErrors(schedulePayload);
    setIsValidating(false); // Hide spinner after API call
    if (errors.length === 0) {
      // If no more errors, proceed with scheduling
      const userConfirmed = window.confirm(
        "Are you sure you want to schedule these ads?"
      );

      if (userConfirmed) {
        // Get the current date/time for scheduling
        const scheduledDate = new Date();

        // Set the default date for the modal
        setScheduledDateForModal(scheduledDate);

        // Open the modal
        setIsScheduleModalOpen(true);
      }
    } else {
      // If errors still exist, show the validation modal again
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
    }
  };

  const handlePublishValidationResolve = async () => {
    setIsPublishValidationModalOpen(false);
    handlePublish(true);
  };

  // Function to handle the date selection from modal
  const handleScheduleDateConfirm = async (scheduledDateTime) => {
    // Validate the date
    if (isNaN(scheduledDateTime.getTime()) || scheduledDateTime <= new Date()) {
      notify("Please enter a valid future date and time", "error");
      return;
    }

    setIsScheduleLoading(true);

    // Calculate minutes difference between selected time and current time
    const minutesUntilLaunch = Math.ceil(
      (scheduledDateTime.getTime() - Date.now()) / 60000
    );

    // Prepare the request with the correct format
    const launchData = prepareAdLaunchData();

    if (launchData.length === 0) {
      notify("Failed to prepare ad launch data", "error");
      setIsScheduleLoading(false);
      return;
    }

    // Add launchInMinutes to each ad object
    const schedulePayload = {
      adObjects: launchData.map((adObject) => ({
        ...adObject,
        launchInMinutes: minutesUntilLaunch,
      })),
    };

    try {
      // Send the payload to the API
      const response = await handleSaveAdLaunches(schedulePayload);

      if (response && response.batch_id) {

        // Display a success message with time until launch
        const timeText =
          minutesUntilLaunch === 1
            ? "1 minute"
            : minutesUntilLaunch < 60
              ? `${minutesUntilLaunch} minutes`
              : `${Math.floor(minutesUntilLaunch / 60)} hour${Math.floor(minutesUntilLaunch / 60) !== 1 ? "s" : ""
              } and ${minutesUntilLaunch % 60} minute${minutesUntilLaunch % 60 !== 1 ? "s" : ""
              }`;

        navigate("/ads-progress", {
          state: {
            batchId: response.batch_id,
            launches: response.launches,
            isScheduled: true,
            scheduledTime: scheduledDateTime.toISOString(),
            scheduledUnixTime: Math.floor(scheduledDateTime.getTime() / 1000),
            launchInMinutes: minutesUntilLaunch,
            displayScheduledTime: timeText
          },
        });

        // notify(
        //   `Ads scheduled successfully for ${scheduledDateTime.toLocaleString()} (in ${timeText})`,
        //   "success"
        // );
        setIsScheduleModalOpen(false);
        // navigate("/launch-history");
      } else {
        notify("Failed to schedule ad launches. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error during ad scheduling:", error);
      notify("An error occurred while scheduling ad launches", "error");
    } finally {
      setIsScheduleLoading(false);
    }
  };

  const getAdSetsForAd = (adId) => {
    const arrOfAdsetNames = [
      ...new Set(
        adAndAdsetMapping
          .filter((mapping) => mapping.adId === adId)
          .map((mapping) => mapping.adsetName)
      ),
    ];
    return arrOfAdsetNames;
  };

  const selectedCount = Object.values(selectedAds).filter(Boolean).length;

  // Sync BulkEditor selection → floating bar
  const handleBulkEditorSelectionChange = (campaignId, campaign, adsetId, adset) => {
    setFloatingSelection({ campaignId, campaign, adsetId, adset });
  };

  // Apply from floating bar uses the same handler as the top BulkEditor
  const handleFloatingApply = () => {
    handleApply(
      floatingSelection.adsetId,
      floatingSelection.adset,
      floatingSelection.campaignId,
      floatingSelection.campaign
    );
  };

  // effects

  useEffect(() => {
    if (userId) {
      handleGetUserById(userId);
    }
  }, [userId]);
  useEffect(() => {
    if (defaultValues) {
      handleGetAdsWithCreatives(userId, defaultValues?.creativeLocationId);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (userId && adAccountId)
      handleGetDefaultValues(userId, adAccountId);
  }, [userId, adAccountId]);

  // default mapping
  useEffect(() => {
    if (ads) {

      // check if local storage has existing mapping
      const existingAdAdsetMapping = localStorage.getItem("existingAdAdsetMapping");
      if (existingAdAdsetMapping) {
        setAdAndAdsetMapping(JSON.parse(existingAdAdsetMapping));
        return;
      }
      
      const initialMapping = ads?.map((ad) => ({
        adId: ad._id,
        adsetId: targetAdsetId,
        adsetName: targetAdsetName,
        fbCampaignId: targetCampaignId,
        campaignName: targetCampaignName,
        campaignType: campaignObjective || "OUTCOME_SALES",
      }));

      setAdAndAdsetMapping(initialMapping);
    }
    
  }, [ads]);

  // Show floating bottom bar once the BulkEditor sentinel scrolls out of view
  useEffect(() => {
    const sentinel = bulkEditorSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowFloatingBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setLoading(true);

    // Check if campaigns and campaigns.data are defined
    if (!campaigns || !campaigns.data) {
      setLoading(false);
      return;
    }

    const targetCampaign = campaigns.data.find(
      (campaign) => campaign.id === targetCampaignId
    );

    if (!targetCampaign) {
      console.warn("Target campaign not found:", targetCampaignId);
      setLoading(false);
      return;
    }

    const initialCampaignName = targetCampaign.name;

    // Check if ads is defined and is an array
    if (ads && Array.isArray(ads)) {
      const initialCampaignNames = ads.map(() => initialCampaignName);
      setCampaignNames(initialCampaignNames);
    } else {
      console.warn("Ads is not defined or is not an array");
    }

    setLoading(false);
  }, [campaigns, targetCampaignId, ads]);

  return (
    <div className="flex flex-col gap-4 px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
      {(loading || isValidating || isPublishValidating) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      )}

      <Navbar
        title={"Advanced Mapping"}
        showBack={true}
        saveButtonText="Save and Next"
        onSaveAndNext={() => handlePublish(true)}
        // onSchedule={handleScheduleAds}
        // scheduleButtonText="Schedule Publish Ads"
        // showSchedule={true}
        adAccountName={adAccountName}
        loading={isScheduleModalOpen || isScheduleLoading}
      />
      {/* Sentinel — IntersectionObserver watches this to know when BulkEditor leaves the viewport */}
      <div ref={bulkEditorSentinelRef} className="h-0 w-full" aria-hidden="true" />
      <BulkEditor
        onApply={handleApply}
        overwriteExisting={overwriteExisting}
        setOverwriteExisting={setOverwriteExisting}
        applyDisabled={selectedCount === 0}
        onSelectionChange={handleBulkEditorSelectionChange}
      />

      {/* table — extra bottom padding when floating bar is visible so last rows aren't hidden */}
      <div className={`bg-white border rounded-lg shadow-sm${showFloatingBar ? " mb-20" : ""}`}>
        {/* table header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-4">
            <input
              id="select-all"
              name="select-all"
              type="checkbox"
              checked={allSelected}
              onChange={handleToggleAll}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="select-all" className="ml-2 text-sm text-gray-700">
              Select All
            </label>
          </div>
          {selectedCount > 0 && (
            <div className="text-sm text-gray-600">
              {selectedCount} asset{selectedCount !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>

        <div className="divide-y divide-gray-200">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 text-sm font-medium text-gray-700 bg-gray-50">
            <div className="col-span-1 text-center">Select</div>
            <div className="col-span-3 mr-12 text-center">Ad</div>
            <div className="col-span-3">Ad Name</div>
            <div className="col-span-4">Ad Set</div>
          </div>

          <div className="px-6">
            {ads?.map((ad) => (
              <AssetRow
                key={ad._id}
                asset={{
                  id: ad._id,
                  selected: selectedAds[ad._id] || false,
                  imageUrl: ad.creatives[0].driveThumbnail || `https://drive.google.com/thumbnail?sz=w456-h200&id=${extractDriveId(
                    ad.creatives[0].fileLink
                  )}`,
                  name: ad.name,
                  adSets: getAdSetsForAd(ad._id),
                }}
                onToggleSelect={handleToggleAsset}
                onUpdateAdSets={handleUpdateAdSets}
                campaignNames={campaignNames}
                adAndAdsetMapping={adAndAdsetMapping}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Floating bottom bar ───────────────────────────────────────────────
           Appears once the BulkEditor sentinel scrolls out of view.
           Shows the currently-selected campaign / ad-set and a quick Apply
           button so users never have to scroll back to the top.
      ─────────────────────────────────────────────────────────────────────── */}
      {showFloatingBar && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">

            {/* Selection summary */}
            <div className="flex items-center gap-5 flex-1 min-w-0 overflow-hidden">
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Campaign
                </span>
                <span className="text-sm font-medium text-gray-800 truncate">
                  {floatingSelection.campaign?.name ?? (
                    <span className="text-gray-400 font-normal italic">not selected</span>
                  )}
                </span>
              </div>

              <div className="shrink-0 w-px h-4 bg-gray-200" />

              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Ad Set
                </span>
                <span className="text-sm font-medium text-gray-800 truncate">
                  {floatingSelection.adset?.name ?? (
                    <span className="text-gray-400 font-normal italic">not selected</span>
                  )}
                </span>
              </div>
            </div>

            {/* Right-side actions */}
            <div className="flex items-center gap-3 shrink-0">
              {selectedCount > 0 && (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                  {selectedCount} selected
                </span>
              )}

              {/* Scroll back to the BulkEditor */}
              <button
                type="button"
                onClick={() =>
                  bulkEditorSentinelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                title="Scroll back to Bulk Editor"
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </button>

              {/* Quick Apply */}
              <button
                type="button"
                onClick={handleFloatingApply}
                disabled={
                  selectedCount === 0 ||
                  !floatingSelection.adsetId ||
                  !floatingSelection.campaignId
                }
                className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors shadow-sm
                  ${
                    selectedCount === 0 ||
                    !floatingSelection.adsetId ||
                    !floatingSelection.campaignId
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  }`}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Time Picker Modal */}
      {isScheduleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="p-6 bg-white rounded-lg shadow-lg w-96">
            <h2 className="mb-4 text-xl font-semibold">Schedule Ads</h2>
            <div className="mb-4">
              <label
                htmlFor="scheduleDateTime"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                Select date and time
              </label>
              <input
                id="scheduleDateTime"
                type="datetime-local"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue={scheduledDateForModal ? (() => {
                  const date = scheduledDateForModal;
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })() : (() => {
                  const date = new Date();
                  date.setMinutes(date.getMinutes() + 2);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                min={(() => {
                  const date = new Date();
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })()}
                step="60"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsScheduleModalOpen(false);
                  notify("Scheduling cancelled", "error");
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={isScheduleLoading}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const dateTimeInput = document.getElementById(
                    "scheduleDateTime"
                  );
                  const selectedDateTime = new Date(dateTimeInput.value);
                  handleScheduleDateConfirm(selectedDateTime);
                }}
                className="flex items-center gap-2 px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                disabled={isScheduleLoading}
              >
                {isScheduleLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                    Scheduling...
                  </>
                ) : (
                  'Schedule'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Posting Validation Modal for Scheduling */}
      {isValidationModalOpen && validationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isValidationModalOpen}
          onClose={() => setIsValidationModalOpen(false)}
          errors={validationErrors}
          onEditAds={handleValidationEditAds}
          onResolveErrors={handleValidationResolve}
        />
      )}

      {/* Ad Posting Validation Modal for Publishing */}
      {isPublishValidationModalOpen && publishValidationErrors.length > 0 && (
        <AdPostingValidation
          isOpen={isPublishValidationModalOpen}
          onClose={() => setIsPublishValidationModalOpen(false)}
          errors={publishValidationErrors}
          onEditAds={handlePublishValidationEditAds}
          onResolveErrors={handlePublishValidationResolve}
        />
      )}
    </div>
  );
};

export default AssetMapper;